import { useCallback, useMemo, useState } from "react"

import { UsePopoverSelectProps, UsePopoverSelectRes } from "./types"
import { PopoverComboboxItem } from "../types"

// usePopoverSelect is a hook with a developer-friendly interface for configuring
// a PopoverSelect component. It supports both controlled and uncontrolled management
// of the selected item.
function usePopoverSelect<T>({
  items,
  selected: selectedProp,
  defaultSelected,
  onSelect,
  keyExtractor,
  labelExtractor,
  accessoryExtractor,
  contentExtractor,
  onChangeQuery,
  customItems = [],
  customItemsPlacement = "end",
  selectedKeyOverride,
  selectedLabelOverride,
}: UsePopoverSelectProps<T>): UsePopoverSelectRes<T> {
  const comboboxItems = useMemo<PopoverComboboxItem[]>(() => {
    let comboboxItems: PopoverComboboxItem[] = items.map((item) => ({
      key: keyExtractor(item),
      label: labelExtractor(item),
      accessory: accessoryExtractor?.(item, "list"),
      content: contentExtractor?.(item, "list"),
    }))
    if (customItems.length > 0) {
      const customComboboxItems: PopoverComboboxItem[] = customItems.map(
        (item) =>
          ({
            key: item.key,
            label: item.label,
            content: item.content,
          }) as PopoverComboboxItem
      )
      comboboxItems =
        customItemsPlacement === "start"
          ? [...customComboboxItems, ...comboboxItems]
          : [...comboboxItems, ...customComboboxItems]
    }
    return comboboxItems
  }, [
    items,
    keyExtractor,
    labelExtractor,
    accessoryExtractor,
    contentExtractor,
    customItems,
    customItemsPlacement,
  ])

  const [selectedState, setSelectedState] = useState<T | null>(
    defaultSelected ?? null
  )

  const isControlled = selectedProp !== undefined

  const selected = useMemo<T | null>(() => {
    if (isControlled) {
      return selectedProp
    }
    return selectedState
  }, [isControlled, selectedProp, selectedState])

  const onSelectItem = useCallback(
    (item: T | null) => {
      if (!isControlled) {
        setSelectedState(item)
      }
      onSelect?.(item)
    },
    [isControlled, onSelect]
  )

  const selectedComboboxItem = useMemo<PopoverComboboxItem | null>(() => {
    if (selected === null) {
      if (!selectedKeyOverride) {
        return null
      }
      return (
        comboboxItems.find((item) => item.key === selectedKeyOverride) ?? {
          key: selectedKeyOverride,
          label: selectedLabelOverride ?? "",
        }
      )
    }
    const found = comboboxItems.find(
      (item) => item.key === keyExtractor(selected)
    )
    return {
      key: found?.key ?? keyExtractor(selected),
      label: found?.label ?? labelExtractor(selected),
      accessory: accessoryExtractor?.(selected, "button"),
      content: contentExtractor?.(selected, "button"),
    }
  }, [
    selected,
    comboboxItems,
    keyExtractor,
    labelExtractor,
    accessoryExtractor,
    contentExtractor,
    selectedKeyOverride,
    selectedLabelOverride,
  ])

  const onSelectComboboxItem = useCallback(
    (comboboxItem: PopoverComboboxItem | null) => {
      const customItem =
        customItems && comboboxItem
          ? customItems.find((item) => item.key === comboboxItem.key) ?? null
          : null
      if (customItem) {
        customItem.onSelect()
        return
      }
      const item = comboboxItem
        ? items.find((item) => keyExtractor(item) === comboboxItem.key) ?? null
        : null
      onSelectItem(item)
    },
    [items, onSelectItem, keyExtractor, customItems]
  )

  return {
    popoverSelectProps: {
      items: comboboxItems,
      selected: selectedComboboxItem,
      onSelect: onSelectComboboxItem,
      onChangeQuery,
    },
    selected: isControlled ? selected : selectedState,
  }
}

export default usePopoverSelect
